import React, { useState, useEffect } from 'react';
import { useOrderFlowStore } from '../../../store/orderFlowStore';
import { useTranslations } from '../../../hooks/useTranslations';
import styles from './ProductPreview.module.scss';
import { AnimatePresence, motion } from 'framer-motion';

// windowにwakoFlowPreviewImagesが存在することを示す型定義
// Liquidから渡される実際のデータ構造に合わせてキーを定義します
interface WakoFlowPreviewImages {
  [key: string]: {
    url: string;
    alt: string;
  };
}

declare global {
  interface Window {
    wakoFlowPreviewImages?: WakoFlowPreviewImages;
  }
}

// ダミーデータ。将来的にはwakoCustomFlowDataから取得する
const previewImages = {
  'freeze-dried': 'https://placehold.jp/800x800.png?text=Freeze-dried',
  'liquid-concentrate':
    'https://placehold.jp/800x800.png?text=Liquid-concentrate',
  'straight-coffee':
    'https://placehold.jp/800x800.png?text=Straight-coffee',
};

export const ProductPreview: React.FC = () => {
  // 1. Zustandストアからユーザーの選択状態を取得
  const { 
    productType, 
    beanType, 
    selectedBean, 
    packageType, 
    sugarConcentration,
    needsInnerDesign,
    needsFrontLabel,
    needsOuterBox,
    needsOuterBoxDesign,
    needsBackLabel,
    userSelections
  } = useOrderFlowStore();

  const translations = useTranslations();

  // 2. 表示する画像レイヤーのリストを管理
  const [imageLayers, setImageLayers] = useState<
    { key: string; url: string; alt: string }[]
  >([]);

  // 3. 選択肢の変更を監視し、表示する画像レイヤーのリストを更新
  useEffect(() => {
    const newLayers: { key: string; url: string; alt: string }[] = [];
    const previewImages = window.wakoFlowPreviewImages;

    if (!previewImages) {
      // フォールバック：デフォルト画像を表示
      newLayers.push({
        key: 'fallback',
        url: 'https://placehold.jp/800x800.png?text=No+Preview+Data',
        alt: translations.preview_noPreviewData
      });
      setImageLayers(newLayers);
      return;
    }

    // 豆関連のキーを特別に表示
    const beanKeys = Object.keys(previewImages).filter(key => key.startsWith('bean_'));
    
    const addLayer = (key: string, alt: string) => {
      if (previewImages[key]) {
        newLayers.push({ 
          key, 
          url: previewImages[key]!.url, 
          alt: previewImages[key]!.alt || alt 
        });
      }
    };
    
    // 表示するレイヤーを決定するロジック（下から上への順序で追加）
    
    // 1. 商品タイプベース画像（最下層）
    if (productType) {
      const productKey = `product_${productType.replace('-', '_')}`;
      addLayer(productKey, `${productType}商品`);
    }
    
    // 2. 豆の種類画像
    if (beanType) {
      let beanKey = '';
      
      if (beanType === 'my-bean') {
        beanKey = 'bean_my_bean';
      } else if (beanType === 'wako' && selectedBean) {
        // WAKOの豆の場合は具体的な銘柄に基づく
        beanKey = `bean_${selectedBean.replace(/-/g, '_')}`;
      } else if (beanType === 'wako' && !selectedBean) {
        // この場合は豆の種類のみを表示
        beanKey = 'bean_wako'; // デフォルトのWAKO画像を使用
      }
      
      if (beanKey) {
        addLayer(beanKey, translations.preview_beanType);
      }
    }
    
    // 3. 砂糖選択画像（濃縮液・ストレートコーヒーのみ）
    if ((productType === 'liquid-concentrate' || productType === 'straight-coffee') && sugarConcentration) {
      const sugarKey = sugarConcentration === 'none' ? 'sugar_none' : 'sugar_added';
      addLayer(sugarKey, translations.preview_sugarOption);
    }

    // 4. デザインオプション画像（ユーザーが実際に選択を行った場合のみ表示）
    if (productType) {
      
      // フリーズドライのデザインオプション
      if (productType === 'freeze-dried') {
        // インナーデザイン（ユーザーが選択した場合のみ）
        if (userSelections.hasSelectedInnerDesign) {
          const innerDesignKey = needsInnerDesign ? 'fd_inner_design_required' : 'fd_inner_design_not_required';
          addLayer(innerDesignKey, translations.preview_innerDesign);
        }
        
        // インナー表ラベル（キューブの場合のみ、ユーザーが選択した場合のみ）
        // 注意: インナー面ラベルは包装画像の上に表示するため、後で追加される
        
        // 外箱（ユーザーが選択した場合のみ）
        if (userSelections.hasSelectedOuterBox) {
          const outerBoxKey = needsOuterBox ? 'fd_outer_box_required' : 'fd_outer_box_not_required';
          addLayer(outerBoxKey, translations.preview_outerBox);
          
          // 外箱デザイン（外箱が必要かつユーザーが選択した場合のみ）
          if (needsOuterBox && userSelections.hasSelectedOuterBoxDesign) {
            const outerBoxDesignKey = needsOuterBoxDesign ? 'fd_outer_box_design_required' : 'fd_outer_box_design_not_required';
            addLayer(outerBoxDesignKey, translations.preview_outerBoxDesign);
          }
        }
      }
      
      // 濃縮液のデザインオプション
      else if (productType === 'liquid-concentrate') {
        // 外箱（ユーザーが選択した場合のみ）
        if (userSelections.hasSelectedOuterBox) {
          const outerBoxKey = needsOuterBox ? 'nk_outer_box_required' : 'nk_outer_box_not_required';
          addLayer(outerBoxKey, translations.preview_outerBox);
          
          // 外箱デザイン（外箱が必要かつユーザーが選択した場合のみ）
          if (needsOuterBox && userSelections.hasSelectedOuterBoxDesign) {
            const outerBoxDesignKey = needsOuterBoxDesign ? 'nk_outer_box_design_required' : 'nk_outer_box_design_not_required';
            addLayer(outerBoxDesignKey, translations.preview_outerBoxDesign);
          }
        }
        
        // 表ラベル（ユーザーが選択した場合のみ）
        // 注意: 表ラベルは包装画像の上に表示するため、後で追加される
      }
      
      // ストレートコーヒーのデザインオプション
      else if (productType === 'straight-coffee') {
        // 外箱（ユーザーが選択した場合のみ）
        if (userSelections.hasSelectedOuterBox) {
          const outerBoxKey = needsOuterBox ? 'st_outer_box_required' : 'st_outer_box_not_required';
          addLayer(outerBoxKey, translations.preview_outerBox);
          
          // 外箱デザイン（外箱が必要かつユーザーが選択した場合のみ）
          if (needsOuterBox && userSelections.hasSelectedOuterBoxDesign) {
            const outerBoxDesignKey = needsOuterBoxDesign ? 'st_outer_box_design_required' : 'st_outer_box_design_not_required';
            addLayer(outerBoxDesignKey, translations.preview_outerBoxDesign);
          }
        }
        
        // 表ラベル（ユーザーが選択した場合のみ）
        // 注意: 表ラベルは包装画像の上に表示するため、後で追加される
      }
      
    }

    // 5. 共通裏ラベル画像（ユーザーが選択した場合のみ）
    if (productType && userSelections.hasSelectedBackLabel) {
      const backLabelKey = needsBackLabel ? 'back_label_required' : 'back_label_not_required';
      addLayer(backLabelKey, translations.preview_backLabel);
    }

    // 6. 包装状態画像
    if (productType && packageType) {
      let packageKey = '';
      
      // 商品タイプ別の包装キーを生成
      if (productType === 'freeze-dried') {
        packageKey = `fd_packing_${packageType}`;
      } else if (productType === 'liquid-concentrate') {
        packageKey = `nk_packing_${packageType}`;
      } else if (productType === 'straight-coffee') {
        packageKey = `st_packing_${packageType}`;
      }
      
      if (packageKey) {
        addLayer(packageKey, translations.preview_packageType);
      }
    }

    // 7. 表ラベル（包装画像の上に表示）
    if (userSelections.hasSelectedFrontLabel) {
      let frontLabelKey = '';
      
      if (productType === 'freeze-dried' && packageType === 'cube') {
        // フリーズドライキューブのインナー面ラベル
        frontLabelKey = needsFrontLabel ? 'fd_inner_front_label_required' : 'fd_inner_front_label_not_required';
        addLayer(frontLabelKey, translations.preview_innerFrontLabel);
      } else if (productType === 'liquid-concentrate') {
        // 濃縮液の表ラベル
        frontLabelKey = needsFrontLabel ? 'nk_front_label_required' : 'nk_front_label_not_required';
        addLayer(frontLabelKey, translations.preview_frontLabel);
      } else if (productType === 'straight-coffee') {
        // ストレートコーヒーの表ラベル
        frontLabelKey = needsFrontLabel ? 'st_front_label_required' : 'st_front_label_not_required';
        addLayer(frontLabelKey, translations.preview_frontLabel);
      }
    }

    // デフォルト画像（何も選択されていない場合）
    if (newLayers.length === 0) {
      addLayer('default', translations.preview_selectProduct);
    }

    // 最終的にまだ画像がない場合のフォールバック
    if (newLayers.length === 0) {
      newLayers.push({
        key: 'final-fallback',
        url: 'https://placehold.jp/800x800.png?text=Final+Fallback',
        alt: translations.preview_finalFallback
      });
    }

    setImageLayers(newLayers);
  }, [
    productType, 
    beanType, 
    selectedBean, 
    packageType, 
    sugarConcentration,
    needsInnerDesign,
    needsFrontLabel,
    needsOuterBox,
    needsOuterBoxDesign,
    needsBackLabel,
    userSelections
  ]);

  // コンポーネントマウント時のデバッグ
  useEffect(() => {
    // 1秒後に再度チェック（Liquidの読み込み完了を待つ）
    setTimeout(() => {
      if (window.wakoFlowPreviewImages) {
        // プレビュー画像データの確認
      }
    }, 1000);
  }, []);

  return (
    <div className={styles.previewContainer}>
      <div className={styles.imageWrapper}>
        {/* 画像レイヤーをアニメーション付きで重ねて表示 */}
        <AnimatePresence>
          {imageLayers.map((layer) => (
            <motion.img
              key={layer.key}
              src={layer.url}
              alt={layer.alt}
              className={styles.previewImageLayer}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
                              onError={(e) => {
                  // エラー時のフォールバック処理
                  e.currentTarget.src = 'https://placehold.jp/800x800.png?text=Image+Error';
                }}
            />
          ))}
        </AnimatePresence>
      </div>
      {/* 将来的に価格表示などを追加 */}
    </div>
  );
}; 